import { useState } from 'react';
import { useAuth } from '../../app/auth/AuthProvider';
import { LiftXLogo } from '../../ui/components/LiftXLogo';
import { CompanyMark } from '../../ui/components/CompanyMark';
import { COMPANY_NAME } from '../../app/appInfo';
import styles from './LoginScreen.module.css';

/**
 * Shown when the server cannot be reached AND there is no verification recent
 * enough to continue on (AUTH-ARCHITECTURE.md §11).
 *
 * Deliberately NOT the login screen. There is no offline login: a password
 * typed here could only be checked against something stored on the device,
 * which is exactly the mechanism this migration removed. Offering a form that
 * cannot work would be dishonest; offering one that "works" locally would be
 * a security regression.
 */
export function OfflineUnverifiableScreen(): React.JSX.Element {
  const { revalidate } = useAuth();
  const [retrying, setRetrying] = useState(false);

  async function handleRetry(): Promise<void> {
    setRetrying(true);
    try {
      await revalidate();
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card} role="status">
        <div className={styles.logoWrap}>
          <LiftXLogo height={44} />
        </div>

        <h1 className={styles.title}>Can&rsquo;t verify offline</h1>
        <p className={styles.subtitle}>
          <CompanyMark size={14} className={styles.subtitleMark} />
          Personal access · {COMPANY_NAME}
        </p>

        <p className={styles.note}>
          LiftX needs to reach the server to confirm your sign-in. Connect to the
          internet and try again.
        </p>

        <button
          type="button"
          className={styles.submit}
          onClick={() => void handleRetry()}
          disabled={retrying}
        >
          {retrying ? 'Checking…' : 'Try again'}
        </button>

        <p className={styles.note}>
          Your training data is safe on this device and untouched. Signing in
          again will bring it straight back.
        </p>
      </div>
    </div>
  );
}
